import {
  PRACTICE_SEGMENT,
  buildChapterExamplesPath,
  buildChapterFlashcardsPath,
  buildChapterTopicPath,
  buildChapterTopicsPath,
  buildChapterVideoPath,
  buildChapterVocabPath,
  buildExamOverviewPath,
  buildExamTipPath,
  buildExampleVideoPath,
  buildExampleVideosPath,
  buildMockExamsPath,
  buildPracticePath,
  buildPracticeSectionPath,
  buildTopicsBasePath,
  buildTopicsDiscussionPath,
  buildTopicsVideoPath,
  normalizeLanguage,
  resolveStoredChapterTrack,
} from './contentRoutes';

export const getLanguageFromPathname = (pathname = '') =>
  pathname.match(/^\/(fr|de)(?=\/|$)/i)?.[1]?.toLowerCase() ?? null;

export const buildLanguageContinuationPath = (location, nextLanguage) => {
  const normalizedLanguage = normalizeLanguage(nextLanguage);
  const pathMatch = location?.pathname?.match(/^\/(fr|de)(\/.*)?$/i);
  const search = location?.search ?? '';
  const hash = location?.hash ?? '';

  if (!pathMatch) {
    return null;
  }

  const suffix = pathMatch[2] || '';

  if (!suffix || suffix === '/') {
    return '/';
  }

  if (suffix === '/fide-exam-overview') {
    return `${buildExamOverviewPath(normalizedLanguage)}${search}${hash}`;
  }

  // Keeps the Oral/Listening tab when switching language, instead of dropping to the default tab.
  // An explicit `/oral` segment has to stay explicit — the bare path is read as "no
  // preference" and can redirect to whichever tab was last used.
  const mockExamsMatch = suffix.match(/^\/mock-exams(?:\/(listening|oral))?$/);
  if (mockExamsMatch) {
    const nextExamType = mockExamsMatch[1];
    return `${buildMockExamsPath(normalizedLanguage, nextExamType, { explicit: nextExamType === 'oral' })}${search}${hash}`;
  }

  const examTipDetailMatch = suffix.match(/^\/exam-tips\/([^/]+)$/);
  if (examTipDetailMatch) {
    return `${buildExamTipPath(normalizedLanguage, examTipDetailMatch[1])}${search}${hash}`;
  }

  if (suffix === '/exam-tips') {
    return `${buildExamTipPath(normalizedLanguage)}${search}${hash}`;
  }

  const topicsVideoMatch = suffix.match(/^\/fide-test-topics\/video\/([^/]+)$/);
  if (topicsVideoMatch) {
    return `${buildChapterVideoPath(normalizedLanguage, 'dialogue', topicsVideoMatch[1])}${hash}`;
  }

  if (suffix === '/fide-test-topics/discussion') {
    return `${buildTopicsDiscussionPath(normalizedLanguage)}${search}${hash}`;
  }

  if (suffix === '/fide-test-b1-discussion') {
    return `${buildTopicsDiscussionPath(normalizedLanguage)}${search}${hash}`;
  }

  if (suffix === '/fide-test-topics') {
    return `${buildTopicsBasePath(normalizedLanguage)}${search}${hash}`;
  }

  if (suffix === '/fide-test-examples') {
    return `${buildChapterExamplesPath(normalizedLanguage, resolveStoredChapterTrack())}${hash}`;
  }

  // Former nested catalog URLs are retained only as redirects; language
  // switching must land on the shorter canonical section URL too.
  if (suffix === `/${PRACTICE_SEGMENT}/a2/telephone/fide-test-examples`) {
    return `${buildPracticeSectionPath(normalizedLanguage, 'a2', 'telephone-roleplay')}${search}${hash}`;
  }

  if (suffix === `/${PRACTICE_SEGMENT}/b1/discussion/fide-test-examples`) {
    return `${buildChapterExamplesPath(normalizedLanguage, 'discussion')}${search}${hash}`;
  }

  // Every practice URL is identical across languages once the /fr|/de prefix is
  // stripped, so the whole tree — map, sections, and the catalogs nested under
  // it — maps 1:1. Keep this generic: a per-shape regex here is the thing that
  // silently drops users back on the topics page when a new route is added.
  if (suffix === `/${PRACTICE_SEGMENT}` || suffix.startsWith(`/${PRACTICE_SEGMENT}/`)) {
    return `/${normalizedLanguage}${suffix}${search}${hash}`;
  }

  const chapterExamplesMatch = suffix.match(/^\/(dialogue|discussion)\/fide-test-examples$/);
  if (chapterExamplesMatch) {
    return chapterExamplesMatch[1] === 'dialogue'
      ? `${buildPracticeSectionPath(normalizedLanguage, 'a2', 'telephone-roleplay')}${hash}`
      : `${buildChapterExamplesPath(normalizedLanguage, chapterExamplesMatch[1])}${hash}`;
  }

  const chapterTopicsMatch = suffix.match(/^\/(dialogue|discussion)\/topics$/);
  if (chapterTopicsMatch) {
    return `${buildChapterTopicsPath(normalizedLanguage, chapterTopicsMatch[1])}${hash}`;
  }

  const chapterTopicDetailMatch = suffix.match(/^\/(dialogue|discussion)\/topics\/([^/]+)$/);
  if (chapterTopicDetailMatch) {
    return `${buildChapterTopicPath(normalizedLanguage, chapterTopicDetailMatch[1], chapterTopicDetailMatch[2])}${hash}`;
  }

  const chapterVideoMatch = suffix.match(/^\/dialogue\/video\/([^/]+)$/);
  if (chapterVideoMatch) {
    return `${buildChapterVideoPath(normalizedLanguage, 'dialogue', chapterVideoMatch[1])}${hash}`;
  }

  const chapterVocabMatch = suffix.match(/^\/(dialogue|discussion)\/vocab\/([^/]+)$/);
  if (chapterVocabMatch) {
    return `${buildChapterVocabPath(normalizedLanguage, chapterVocabMatch[1], chapterVocabMatch[2])}${hash}`;
  }

  const chapterFlashcardsMatch = suffix.match(/^\/(dialogue|discussion)\/flashcards\/([^/]+)$/);
  if (chapterFlashcardsMatch) {
    return `${buildChapterFlashcardsPath(normalizedLanguage, chapterFlashcardsMatch[1], chapterFlashcardsMatch[2])}${hash}`;
  }

  if (suffix === '/fide-test-example-videos') {
    return normalizedLanguage === 'fr'
      ? `${buildExampleVideosPath()}${search}${hash}`
      : buildTopicsBasePath(normalizedLanguage);
  }

  const exampleVideoDetailMatch = suffix.match(/^\/fide-test-example-videos\/([^/]+)$/);
  if (exampleVideoDetailMatch) {
    return normalizedLanguage === 'fr'
      ? `${buildExampleVideoPath(exampleVideoDetailMatch[1])}${search}${hash}`
      : buildTopicsBasePath(normalizedLanguage);
  }

  if (
    suffix === '/fide-vocabulary' ||
    suffix === '/fide-vocab' ||
    suffix === '/fide-flashcards' ||
    suffix === '/flashcards'
  ) {
    return `/${normalizedLanguage}${suffix}${search}${hash}`;
  }

  if (suffix === '/fide-practice') {
    return `${buildPracticePath(normalizedLanguage)}${search}${hash}`;
  }

  return buildTopicsBasePath(normalizedLanguage);
};
